import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Button,
  Checkbox,
  ContextMenu,
  Dropdown,
  Paragraph,
  Preloader,
  Tooltip,
  useContextMenu,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import { isEqual } from 'lodash';
import { createPortal } from 'react-dom';

import { AuthContext } from 'auth';
import {
  EHostModalType,
  HostPageModals,
  QuickMenu,
  Sidebar,
  Statuses,
  useHostModals,
} from 'components';
import {
  CtxMenuHost,
  useHostContextMenuItems,
  useHostsData,
  useMoveHosts,
} from 'hooks';
import { LocalizationContext } from 'localization';
import { IMovedHost } from 'schemas';
import notificationService from 'services/notificationService';
import { ESettingsAliases, SettingsContext } from 'settings';
import { IUser } from 'types';
import { usePrintContext } from 'utils';

import { getContextMenuFunctions } from '../Computers/utilites';
import { MapObjectType, Toolbar } from './Components';
import { useToolConfig } from './Components/Toolbar/constants';
import { ZOOM_OPTIONS } from './constants';
import s from './HostsMap.module.scss';
import { IMapItem, IMapObject, Map } from './Map';
import { DEFAULT_TEXT_STYLE } from './Map/constants';
import { useMapConflict } from './MapConflictContext';

export const HostsMap: FC = () => {
  const [clientUUID, setClientUUID] = useState<string | null>(null);
  const {
    data: { permissions },
  } = useContext(AuthContext);
  const {
    state: {
      settings: { hostMap },
    },
    actions: { setSetting },
  } = useContext(SettingsContext);
  const { hosts, refetch, stopPolling, statedPoling, isFirstLoading } =
    useHostsData();
  const { moveHosts } = useMoveHosts();

  const { isPrinting } = usePrintContext();

  const { t } = useContext(LocalizationContext);
  const mapRef = useRef<HTMLDivElement>(null);

  const [isEdit, setIsEdit] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const hostModalsState = useHostModals(
    true,
    selectedItems,
    hosts,
    setSelectedItems
  );
  const [isDepositOpen, setIsDepositOpen] = useState(false);
  const [defaultClient, setIsDefaultClient] = useState<string | undefined>('');
  const [isResetPasswordModal, setIsResetPasswordModal] = useState<
    boolean | undefined
  >(false);
  const [currentClient, setCurrentClient] = useState<IUser | undefined>(
    undefined
  );
  const [mapObjects, setMapObjects] = useState<Array<IMapObject>>([]);
  const [originalMapObjects, setOriginalMapObjects] = useState<
    Array<IMapObject>
  >([]);
  const [selectedTool, setSelectedTool] = useState<MapObjectType | null>(
    'choose'
  );
  const [mapItems, setMapItems] = useState<Record<number, IMapItem>>({});
  const [isSave, setIsSave] = useState(false);
  const [isMinimal, setIsMinimal] = useState(
    sessionStorage.getItem('isHideObject') === 'true'
  );

  const [zoomValue, setZoomValue] = useState(
    Number(localStorage.getItem('ZOOM_VALUE')) || 1
  );
  const [dropdownVisible, setDropdownVisible] = useState(false);

  const toolConfig = useToolConfig();

  useEffect(() => {
    sessionStorage.setItem('isHideObject', String(isMinimal));
  }, [isMinimal]);

  useEffect(() => {
    const handleKeyClick = (event: KeyboardEvent): void => {
      if (!event.ctrlKey) return;

      if (event.code === 'Digit0') {
        setZoomValue(1);
        return;
      } else if (event.code === 'Equal' && zoomValue < 2) {
        setZoomValue((prev) => prev + 0.25);
        return;
      } else if (event.code === 'Minus' && zoomValue > 0.5) {
        setZoomValue((prev) => prev - 0.25);
        return;
      } else {
        return;
      }
    };

    document.addEventListener('keydown', handleKeyClick);

    return () => document.removeEventListener('keydown', handleKeyClick);
  }, [zoomValue]);

  useEffect(() => {
    localStorage.setItem('ZOOM_VALUE', String(zoomValue));
  }, [zoomValue]);

  useEffect(() => {
    if (Object.keys(mapItems).length > 0) {
      setMapItems((prev) => {
        const newObj: Record<number, IMapItem> = {};

        hosts.forEach((host) => {
          if (prev[host.id]) {
            newObj[host.id] = prev[host.id];
          } else {
            newObj[host.id] = {
              id: host.id,
              x: host.coord_x ?? 0,
              y: host.coord_y ?? 0,
              hostType: host.type,
            };
          }
        });
        return newObj;
      });

      return;
    }

    setMapItems(
      hosts?.reduce((acc, host) => {
        return {
          ...acc,
          [host.id]: {
            id: host.id,
            x: host.coord_x ?? 0,
            y: host.coord_y ?? 0,
            hostType: host.type,
          },
        };
      }, {} as Record<number, IMapItem>)
    );
  }, [hosts]);

  const {
    contextMenuData,
    closeContextMenu,
    cursorPosition,
    onContextMenu,
    onTouchEnd,
    onTouchMove,
    onTouchStart,
  } = useContextMenu<CtxMenuHost>();

  const contextMenuItems = useHostContextMenuItems(
    getContextMenuFunctions(hostModalsState)
  );

  const memoizedContextMenuItems = useMemo(
    () => contextMenuItems,
    [contextMenuItems]
  );

  const openBooking = useCallback((id: number) => {
    hostModalsState.setBookingIdForEdit(id);
    hostModalsState.setType(EHostModalType.BOOKING);
  }, []);

  useEffect(() => {
    if (hostModalsState.selectedHostsIds.length !== 1 || cursorPosition) {
      hostModalsState.setSidebarHostId(null);
    }
  }, [hostModalsState.selectedHostsIds.length, cursorPosition]);

  const addGroup = useCallback(() => {
    hostModalsState.setType(EHostModalType.MANAGE_HOST_GROUP);
  }, []);

  const [notPermissionModalOpen, setNotPermissionModalOpen] =
    useState<boolean>(false);

  const handleCloseDeposit = useCallback(() => {
    setIsDepositOpen(false);
    hostModalsState.setType(EHostModalType.NONE);
    setClientUUID(null);
  }, [hostModalsState]);

  const handleCloseResetPassword = useCallback(() => {
    setIsResetPasswordModal(false);
  }, []);

  const handleCloseClientModal = useCallback(() => {
    setClientUUID(null);
    hostModalsState.unselectHosts();
  }, [hostModalsState]);

  const { setHasConflict } = useMapConflict();

  useEffect(() => {
    if (!hosts.length || !mapObjects.length) {
      setHasConflict(false);
      return;
    }

    const hasConflict = hosts.some(
      (host) =>
        host.coord_x !== null &&
        host.coord_y !== null &&
        mapObjects.some(
          (obj) => obj.x === host.coord_x && obj.y === host.coord_y
        )
    );

    setHasConflict(hasConflict);
  }, [hosts, mapObjects, setHasConflict]);

  useEffect(() => {
    if (!hostMap) return;

    try {
      const parsed =
        typeof hostMap === 'string' ? JSON.parse(hostMap) : hostMap;

      const elements = parsed.elements || [];
      if (elements.length === 0) return;

      const loadedObjects = elements.map((el: any) => ({
        id: Number(el.id),
        type: el.type,
        x: Number(el.coord_x),
        y: Number(el.coord_y),
        rotation: el.angle ?? 0,
        text: el.content || undefined,
        textStyle: el.contentStyle
          ? el.contentStyle
          : el.content
          ? DEFAULT_TEXT_STYLE
          : undefined,
      }));

      setMapObjects(loadedObjects);
      setOriginalMapObjects(loadedObjects);
    } catch (err) {
      console.error('Ошибка загрузки планировки:', err);
    }
  }, [hostMap]);

  const saveToBackend = useCallback(() => {
    const elements = mapObjects.map((obj) => ({
      id: obj.id,
      type: obj.type,
      coord_x: obj.x,
      coord_y: obj.y,
      angle: obj.rotation ?? 0,
      ...(obj.text ? { content: obj.text } : {}),
      ...(obj.textStyle ? { contentStyle: obj.textStyle } : {}),
    }));

    return setSetting({
      alias: ESettingsAliases.HOST_MAP,
      value: JSON.stringify({ elements }),
    })
      .then(() => setOriginalMapObjects(mapObjects))
      .catch((err) => {
        notificationService.info({
          t,
          type: 'error',
          message: err?.message,
          title: t('errors.error'),
        });
      });
  }, [setSetting, mapObjects]);

  const saveMap = useCallback(() => {
    stopPolling();
    const savedHost: Array<IMovedHost> = [];
    const isEqualObj = isEqual(mapObjects, originalMapObjects);

    hosts?.forEach((host) => {
      const { id, coord_y, coord_x } = host;

      const { x, y } = mapItems[id];

      if (x !== coord_x || y !== coord_y) {
        savedHost.push({
          coord_x: x,
          coord_y: y,
          id,
        });
      }
    });

    if (savedHost.length) {
      setIsSave(true);
      moveHosts({
        variables: {
          input: savedHost,
        },
      })
        .then((res) => {
          if (!res?.data) {
            throw new Error(
              res?.errors?.[0]?.message || t('map.default_error')
            );
          } else if (!isEqualObj) {
            return saveToBackend();
          } else {
            return;
          }
        })
        .catch((err) => {
          notificationService.info({
            t,
            type: 'error',
            message: err?.message,
            title: t('errors.error'),
          });
          setMapObjects(originalMapObjects);
          setMapItems(
            hosts?.reduce((acc, host) => {
              return {
                ...acc,
                [host.id]: {
                  id: host.id,
                  x: host.coord_x ?? 0,
                  y: host.coord_y ?? 0,
                  hostType: host.type,
                },
              };
            }, {} as Record<number, IMapItem>)
          );
        })
        .finally(() => {
          refetch();
          statedPoling();
          setIsSave(false);
          setIsEdit(false);
        });
    } else if (!isEqualObj) {
      setIsSave(true);
      saveToBackend().finally(() => {
        setIsSave(false);
        setIsEdit(false);
      });
    } else {
      setIsEdit(false);
    }
  }, [
    hosts,
    mapItems,
    mapObjects,
    originalMapObjects,
    saveToBackend,
    stopPolling,
    t,
  ]);

  if (isPrinting) {
    return null;
  }

  return (
    <Preloader loading={isFirstLoading}>
      <div className={s.container}>
        <HostPageModals
          notPermissionModalOpen={notPermissionModalOpen}
          isDepositOpen={isDepositOpen}
          setIsDepositOpen={setIsDepositOpen}
          setIsDefaultClient={setIsDefaultClient}
          isResetPasswordModal={isResetPasswordModal}
          setIsResetPasswordModal={setIsResetPasswordModal}
          selectedClientUUID={clientUUID}
          defaultClient={defaultClient}
          currentClient={currentClient}
          setCurrentClient={setCurrentClient}
          onCloseDeposit={handleCloseDeposit}
          onCloseResetPassword={handleCloseResetPassword}
          onCloseClientModal={handleCloseClientModal}
          onCloseNotPermission={() => setNotPermissionModalOpen(false)}
          hostModalsState={hostModalsState}
          refetch={refetch}
          stopPolling={stopPolling}
          statedPoling={statedPoling}
        />
        {hostModalsState.sidebarHostId && (
          <Sidebar
            hostId={hostModalsState.sidebarHostId}
            onClose={() => {
              hostModalsState.unselectHosts();
              hostModalsState.setSidebarHostId(null);
            }}
          />
        )}
        {contextMenuData &&
          cursorPosition &&
          (() => {
            const modalRoot = document.getElementById('modal-root');

            if (!modalRoot) return null;

            return createPortal(
              <ContextMenu
                layoutElement={mapRef.current ?? undefined}
                data={contextMenuData}
                items={memoizedContextMenuItems}
                closeContextMenu={closeContextMenu}
                position={cursorPosition}
              />,
              modalRoot
            );
          })()}
        <div className={s.top}>
          {!isEdit && <Statuses />}
          {isEdit && (
            <span className={s.choose_action}>
              {t('host.map.choose.action')}{' '}
              {toolConfig.find((tool) => tool.type === selectedTool)?.key}
            </span>
          )}
          {permissions?.has('host_manage') && (
            <div className={cn(s.right, isEdit && s.edit)}>
              {isEdit && (
                <Toolbar
                  selectedTool={selectedTool}
                  onToolSelect={setSelectedTool}
                />
              )}
              <div className={s.buttons_block}>
                {isEdit && (
                  <Button
                    onClick={addGroup}
                    iconName={'CirclePlusIcon'}
                    disabled={isSave}
                  >
                    {t('group.create')}
                  </Button>
                )}
                <Tooltip
                  content={isEdit ? t('map.save') : t('map.edit')}
                  contentClassName={s.button_tooltip}
                  position="bottom-right"
                >
                  <Button
                    variant="secondary"
                    iconName={isEdit ? 'SaveIcon' : 'EditIcon'}
                    onClick={() => {
                      if (!isEdit) {
                        setIsEdit(true);
                      } else {
                        saveMap();
                      }
                    }}
                    className={s.button}
                    onlyIcon
                    isLoading={isSave}
                  />
                </Tooltip>
              </div>
            </div>
          )}
        </div>
        {!isEdit && (
          <Checkbox
            value={isMinimal}
            onChange={() => setIsMinimal(!isMinimal)}
            label={t('map.onlyHost')}
          />
        )}
        <Map
          ref={mapRef}
          mapItems={Object.values(mapItems)}
          onHostClick={hostModalsState.onHostClick}
          selectedHostsIds={hostModalsState.selectedHostsIds}
          onHostDoubleClick={hostModalsState.openShop}
          openBooking={openBooking}
          onContextMenu={onContextMenu}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          openHostCreate={(hostCoords) => {
            hostModalsState.unselectHosts();
            hostModalsState.setType(EHostModalType.MANAGE_HOST);
            hostModalsState.setHostCoords(hostCoords);
          }}
          canOpenTooltip={!cursorPosition}
          setClientUUID={(clientUUID) => {
            if (!permissions?.has('client_info')) {
              setNotPermissionModalOpen(true);
              return;
            }

            setClientUUID(clientUUID);
          }}
          isEdit={isEdit}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          hosts={hosts}
          hostModalsState={hostModalsState}
          selectedTool={selectedTool}
          onToolSelect={setSelectedTool}
          mapObjects={mapObjects}
          onMapObjectsChange={setMapObjects}
          setMapItems={setMapItems}
          isSave={isSave}
          isMinimal={isMinimal}
          setOriginalMapObjects={setOriginalMapObjects}
          zoomValue={zoomValue}
        />
        {!isEdit && (
          <div className={s.zoom}>
            {dropdownVisible && (
              <Dropdown
                items={ZOOM_OPTIONS}
                onChange={(value) => {
                  setZoomValue(value);
                  setDropdownVisible(false);
                }}
                renderItem={(item) => (
                  <div
                    className={cn(
                      s.item,
                      item.value === zoomValue && s.item_current
                    )}
                  >
                    {item.title}
                    {item.value === 1 && (
                      <Paragraph color="secondary">Ctrl + 0</Paragraph>
                    )}
                  </div>
                )}
                className={s.dropdown}
              />
            )}

            <div className={s.zoom__control}>
              <Button
                onlyIcon
                variant="ghost"
                iconName="MinusIcon"
                disabled={zoomValue === 0.5}
                className={s.zoom__button__control}
                onClick={() => setZoomValue((prev) => prev - 0.25)}
              />
              <Button
                variant="ghost"
                onClick={() => setDropdownVisible(!dropdownVisible)}
              >{`${zoomValue * 100}%`}</Button>
              <Button
                onlyIcon
                variant="ghost"
                iconName="PlusIcon"
                disabled={zoomValue === 2}
                className={s.zoom__button__control}
                onClick={() => setZoomValue((prev) => prev + 0.25)}
              />
            </div>
          </div>
        )}
        {selectedItems.size > 1 && (
          <QuickMenu
            hosts={hosts}
            selectedItems={selectedItems}
            onClose={() => setSelectedItems(new Set())}
            openStartSeanceModal={() =>
              hostModalsState.setType(EHostModalType.SHOP)
            }
            openBookingModal={() =>
              hostModalsState.setType(EHostModalType.BOOKING)
            }
            openSendMessageModal={() =>
              hostModalsState.setType(EHostModalType.SEND_MESSAGE)
            }
          />
        )}
      </div>
    </Preloader>
  );
};
